// Copyright ©️ 2026 eVolve MEP, LLC

import type { RefObject } from 'react';

import { Combobox, ScrollArea, type ComboboxStore, type Primitive } from '@mantine/core';

import { SelectFooter } from 'components/Mantine/TypeSafeSelect/SelectFooter';
import type { WrappedSelectProps, WrappedMultiSelectProps } from 'components/Mantine/TypeSafeSelect/types';
import type { useWrappedSelect } from 'components/Mantine/TypeSafeSelect/useWrappedSelect';
import type { WithRequiredIsh } from 'helpers/helper-types';
import { TriggerOnVisible } from 'hooks/useOnScreen';

type Props<T extends Primitive, P extends WrappedSelectProps<T, object> | WrappedMultiSelectProps<T, object>> = {
  ref: RefObject<HTMLDivElement | null>;
  combobox: ComboboxStore;
  options: ReturnType<typeof useWrappedSelect>['options'];
} & WithRequiredIsh<
  Pick<
    P,
    | 'loading'
    | 'maxDropdownHeight'
    | 'autoDropdownWidth'
    | 'onScrollToEnd'
    | 'error'
    | 'nothingFoundMessage'
    | 'includeCloseButton'
    | 'footer'
  >
>;

export const SelectDropdown = <
  T extends Primitive,
  P extends WrappedSelectProps<T, object> | WrappedMultiSelectProps<T, object>,
>({
  ref,
  combobox,
  options,
  loading,
  maxDropdownHeight,
  autoDropdownWidth,
  onScrollToEnd,
  nothingFoundMessage,
  includeCloseButton,
  footer,
  error,
}: Props<T, P>) => (
  <Combobox.Dropdown
    mt={error ? 20 : undefined}
    style={{
      maxWidth: `min(calc(100vw - 100px), ${(autoDropdownWidth ? null : ref.current?.clientWidth) ?? 620}px)`,
    }}
  >
    <Combobox.Options
      style={
        autoDropdownWidth
          ? {
              minWidth: ref.current?.clientWidth ?? 200,
              width: 'max(100%, max-content)',
              whiteSpace: 'nowrap',
            }
          : undefined
      }
    >
      <ScrollArea.Autosize mah={maxDropdownHeight ?? 200} type="auto" scrollbars="y">
        {loading ? (
          <Combobox.Empty>Loading...</Combobox.Empty>
        ) : options.length === 0 ? (
          <Combobox.Empty>{nothingFoundMessage}</Combobox.Empty>
        ) : (
          options
        )}
        {!!onScrollToEnd && <TriggerOnVisible onVisible={onScrollToEnd} />}
      </ScrollArea.Autosize>
    </Combobox.Options>
    <SelectFooter includeCloseButton={includeCloseButton} footer={footer} closeDropdown={combobox.closeDropdown} />
  </Combobox.Dropdown>
);
